import "./CommitPopupComponent.scss";
import {useCallback, useState} from "react";
import commonService from "@/services/common.service.ts";
import {Input} from "@/components/ui/input.tsx";
import {Button} from "@/components/ui/button.tsx";
import {useSelector} from "react-redux";
import {IRootReducerState} from "@/store/reducers";
import {useAppDispatch} from "@/store/hooks.ts";
import {setCodeChanged, setProjectCode} from "@/store/actions/projectdata.action.ts";

interface CommitPopupComponentProps {
    onClose: () => void;
    projectId: string;
    onCodeSave: () => void;
    defaultCommitMessage?: string;
}

const CommitPopupComponent = (props: CommitPopupComponentProps) => {
    const {onClose, projectId, onCodeSave, defaultCommitMessage} = props;

    const [commitMessage, setCommitMessage] = useState(defaultCommitMessage || '')
    const [isCodeGettingSaved, setIsCodeGettingSaved] = useState(false)
    const {tempCode} = useSelector((state: IRootReducerState) => state.projectData);
    const dispatch = useAppDispatch();

    const updateCode = useCallback(() => {
        setIsCodeGettingSaved(true)
        const payload = {
            data: JSON.parse(tempCode || ''),
            title: commitMessage,
        }
        commonService._project_service.updateCode(projectId, payload)
            .then(() => {
                commonService.handleMixpanelTrack("Code saved successfully", {
                    project_id: projectId,
                });
                setIsCodeGettingSaved(false)
                dispatch(setProjectCode(tempCode || ""))
                onCodeSave()
                dispatch(setCodeChanged(false))
                setCommitMessage("");  // Reset commit message on cancel
            })
            .catch(() => {
                setIsCodeGettingSaved(false)
                commonService.handleMixpanelTrack("Code update failed", {
                    project_id: projectId,
                    error_message: "Error updating code",
                    timestamp: new Date().toISOString()
                });
                commonService._alert.showToast("Error updating code", "error")
            });
    }, [onCodeSave, projectId, commitMessage, dispatch, tempCode])

    const handleCancel = useCallback(() => {
        setCommitMessage("");  // Reset commit message on cancel
        onClose();  // Close the modal
    }, [onClose])

    return (
        <div className="project-add-form-wrapper">
            <div className={'text-2xl font-medium mb-6'}>Please Mark Your Changes.</div>
            <div style={{marginBottom: '16px'}}>
                <Input
                    label="Title to remember"
                    value={commitMessage}
                    onChange={(name: string) => setCommitMessage(name)}
                    fullWidth={true}
                    required={true}
                    onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === 'Enter' && commitMessage.trim()) {
                            e.preventDefault();
                            updateCode();
                        }
                    }}
                />
            </div>
            <div className="project-add-actions">
                <Button
                    className="project-item-button"
                    onClick={handleCancel}
                    variant="outline"
                    disabled={isCodeGettingSaved}
                >
                    Cancel
                </Button>
                <Button
                    className="project-item-button"
                    onClick={updateCode}
                    loading={isCodeGettingSaved}
                    disabled={!commitMessage}
                >
                    Save Changes
                </Button>
            </div>
        </div>
    );

};

export default CommitPopupComponent;
